import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useRefreshRelatedData } from '@/hooks/useRefreshRelatedData';
import { showApiErrorToast } from '@/utils/errors';
import { approveDcr, approveDer, approveExpense } from '../api/billing.api';
import type {
  ApproveDcrPayload,
  ApproveDerPayload,
  ApproveExpensePayload,
} from '../types/billing.types';

const MESSAGE = {
  approve: { success: 'Approved', failure: 'Could not approve this' },
  reject: { success: 'Rejected', failure: 'Could not reject this' },
};

function messageFor(accept: boolean): { success: string; failure: string } {
  return accept ? MESSAGE.approve : MESSAGE.reject;
}

export function useApproveDcr() {
  const refreshRelatedData = useRefreshRelatedData();

  return useMutation({
    mutationFn: ({ recordId, payload }: { recordId: number; payload: ApproveDcrPayload }) =>
      approveDcr(recordId, payload),
    onSuccess: (_data, { payload }) => {
      void refreshRelatedData('billingEntry');
      toast.success(messageFor(payload.accept).success);
    },
    onError: (error, { payload }) => {
      showApiErrorToast(error, messageFor(payload.accept).failure);
    },
  });
}

export function useApproveDer() {
  const refreshRelatedData = useRefreshRelatedData();

  return useMutation({
    mutationFn: ({ detailId, payload }: { detailId: number; payload: ApproveDerPayload }) =>
      approveDer(detailId, payload),
    onSuccess: (_data, { payload }) => {
      void refreshRelatedData('billingEntry');
      toast.success(messageFor(payload.accept).success);
    },
    onError: (error, { payload }) => {
      showApiErrorToast(error, messageFor(payload.accept).failure);
    },
  });
}

export function useApproveExpense() {
  const refreshRelatedData = useRefreshRelatedData();

  return useMutation({
    mutationFn: ({ expenseId, payload }: { expenseId: number; payload: ApproveExpensePayload }) =>
      approveExpense(expenseId, payload),
    onSuccess: (_data, { payload }) => {
      void refreshRelatedData('billingEntry');
      toast.success(messageFor(payload.accept).success);
    },
    onError: (error, { payload }) => {
      showApiErrorToast(error, messageFor(payload.accept).failure);
    },
  });
}
